import { homeCarePartnerDescriptor } from "@sp-crm/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ExternalRecordLink } from "components/home-care-leads/send-to-partner/external-record-link";
import { SendSectionList } from "components/home-care-leads/send-to-partner/send-preview";
import {
    LoadingDetails,
    PanelActions,
} from "components/home-care-leads/send-to-partner/send-to-partner-panel";
import {
    PartnerSyncNowButton,
    PartnerSyncSummary,
    usePartnerSyncClock,
} from "components/partner-sync/partner-sync-status";
import { trpc } from "components/rpc/trpc";
import { InlineBanner } from "components/ui/inline-banner";
import { PanelType } from "components/ui/panel/panel-type";
import { ResponsivePanel } from "components/ui/panel/responsive-panel";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import React, { useCallback, useEffect, useState } from "react";
import {
    useRememberWellSkyReferralSourceStatus,
    useWellSkyReferralSourceStatus,
    useWellSkyReferralSourceSyncNow,
    WellSkyReferralSourceRef,
    WellSkyReferralSourceStatus,
} from "./wellsky-referral-source-sync";

const { partnerName } = homeCarePartnerDescriptor("wellSkyPersonalCare");

interface SendReferralSourceToWellSkyPanelProps {
    referralSource: WellSkyReferralSourceRef | null;
    onDismiss: () => void;
}

export const SendReferralSourceToWellSkyPanel: React.FC<
    SendReferralSourceToWellSkyPanelProps
> = ({ referralSource, onDismiss }) => {
    const [opened, setOpened] = useState<WellSkyReferralSourceRef | null>(null);

    useEffect(() => {
        if (referralSource !== null) {
            setOpened(referralSource);
        }
    }, [referralSource]);

    return (
        <ResponsivePanel
            isOpen={referralSource !== null}
            onDismiss={onDismiss}
            type={PanelType.medium}
            headerText={`Send to ${partnerName}`}>
            {opened ? (
                <SendReferralSourceToWellSkyBody
                    referralSource={opened}
                    onDismiss={onDismiss}
                />
            ) : null}
        </ResponsivePanel>
    );
};

interface SendReferralSourceToWellSkyBodyProps {
    referralSource: WellSkyReferralSourceRef;
    onDismiss: () => void;
}

const SendReferralSourceToWellSkyBody: React.FC<SendReferralSourceToWellSkyBodyProps> = ({
    referralSource,
    onDismiss,
}) => {
    const statusQuery = useWellSkyReferralSourceStatus(referralSource);

    if (
        statusQuery.isError ||
        (statusQuery.isPending && statusQuery.fetchStatus === "idle")
    ) {
        return (
            <InlineBanner type="error">
                We could not check whether this referral source was already sent to{" "}
                {partnerName}. Close the panel and try again.
            </InlineBanner>
        );
    }

    if (statusQuery.isPending) {
        return <LoadingDetails />;
    }

    if (statusQuery.data) {
        return (
            <LinkedReferralSourceSync
                referralSource={referralSource}
                status={statusQuery.data}
                dataUpdatedAt={statusQuery.dataUpdatedAt}
                onDismiss={onDismiss}
            />
        );
    }

    return <SendReferralSourcePreview referralSource={referralSource} />;
};

interface LinkedReferralSourceSyncProps {
    referralSource: WellSkyReferralSourceRef;
    status: WellSkyReferralSourceStatus;
    dataUpdatedAt: number;
    onDismiss: () => void;
}

const LinkedReferralSourceSync: React.FC<LinkedReferralSourceSyncProps> = ({
    referralSource,
    status,
    dataUpdatedAt,
    onDismiss,
}) => {
    const syncNow = useWellSkyReferralSourceSyncNow(referralSource);
    const now = usePartnerSyncClock(dataUpdatedAt);
    return (
        <div className="space-y-3">
            <p className="text-sm text-gray-700">
                This referral source is linked to {partnerName}. From now on, changes made
                here update {partnerName} automatically.
            </p>
            <PartnerSyncSummary
                current={status.sync.current}
                partnerName={partnerName}
                now={now}
            />
            {status.url ? (
                <ExternalRecordLink url={status.url} partnerName={partnerName} />
            ) : null}
            {syncNow.error ? (
                <InlineBanner type="error">{syncNow.error}</InlineBanner>
            ) : null}
            <PanelActions>
                <SecondaryButton onClick={onDismiss}>Close</SecondaryButton>
                <PartnerSyncNowButton current={status.sync.current} syncNow={syncNow} />
            </PanelActions>
        </div>
    );
};

const SendReferralSourcePreview: React.FC<{
    referralSource: WellSkyReferralSourceRef;
}> = ({ referralSource }) => {
    const trpcContext = trpc.useTRPC();
    const [sendError, setSendError] = useState<string | null>(null);
    const rememberStatus = useRememberWellSkyReferralSourceStatus(referralSource);

    const previewQuery = useQuery(
        trpcContext.wellSkyReferralSource.preview.queryOptions({ referralSource }),
    );

    const sendMutation = useMutation({
        ...trpcContext.wellSkyReferralSource.send.mutationOptions(),
        onSuccess: status => {
            setSendError(null);
            rememberStatus(status);
        },
        onError: error => {
            setSendError(
                error instanceof Error
                    ? error.message
                    : `Unable to send to ${partnerName}.`,
            );
        },
    });

    const handleSend = useCallback(() => {
        sendMutation.mutate({ referralSource });
    }, [sendMutation, referralSource]);

    return (
        <div className="space-y-4">
            {sendError ? <InlineBanner type="error">{sendError}</InlineBanner> : null}
            <p className="text-sm text-gray-700">
                These details will be sent to {partnerName}:
            </p>
            {previewQuery.isPending ? (
                <LoadingDetails />
            ) : previewQuery.isError || !previewQuery.data ? (
                <InlineBanner type="error">
                    We could not load the details for this referral source, so there is
                    nothing to send to {partnerName} yet. Close the panel and try again.
                </InlineBanner>
            ) : (
                <SendSectionList sections={previewQuery.data.sections} />
            )}
            <p className="text-sm text-gray-700">
                If {partnerName} already has a referral source with this name, it is
                linked instead of added. From now on, changes made here update{" "}
                {partnerName} automatically.
            </p>
            <PanelActions>
                <PrimaryButton
                    onClick={handleSend}
                    disabled={
                        previewQuery.isPending ||
                        previewQuery.isError ||
                        sendMutation.isPending
                    }>
                    Send to {partnerName}
                </PrimaryButton>
            </PanelActions>
        </div>
    );
};
